/**
 * Drata public API payload subsets used by the desktop adapter AND the sim.
 *
 * real-providers change: the adapter talks to the verified V2 API (`.context/PROVIDER_API_NOTES.md`
 * section 2). Every object schema is tolerant: a `z.preprocess` step aliases the V2 field names
 * (`flags.isReady`, `user.email`, `complianceChecks[]`, numeric ids, ...) onto the legacy names the
 * normalizers rely on, objects are `.loose()`, status fields are plain strings (unlisted values map
 * to UNKNOWN downstream, never PASS) and timestamps are tolerant. The legacy fixture/sim shapes
 * (V1-style, string ids) still parse unchanged.
 */
import { z } from 'zod';
import { isRawObject, looseDateTime, looseUrl, pickFirst, rawIdString } from './vanta.js';

const isoDateTime = looseDateTime;
const rawStatus = z.string().nullable();

function idList(v: unknown): string[] {
  return Array.isArray(v) ? v.map(rawIdString).filter((s): s is string => s !== undefined) : [];
}

/** Ids of `{id}` objects in an array or in a `{data: [...]}` expand envelope. */
function idsOfObjects(v: unknown): string[] {
  const list = Array.isArray(v) ? v : isRawObject(v) && Array.isArray(v.data) ? v.data : [];
  return list.map((item) => (isRawObject(item) ? rawIdString(item.id) : rawIdString(item))).filter((s): s is string => s !== undefined);
}

export const DrataPageMetaSchema = z
  .object({
    cursor: z.string().nullable().optional(),
    nextCursor: z.string().nullable().optional(),
    total: z.number().int().nonnegative().nullable().optional(),
    limit: z.number().int().positive().optional(),
  })
  .loose();
export type DrataPageMeta = z.infer<typeof DrataPageMetaSchema>;

/**
 * Cursor envelope. real-providers change: V2 answers `{data, pagination:{cursor,totalCount}}` (plus
 * `lastCheck` on failures); the legacy `{data, page:{cursor,nextCursor,total}}` still parses. The
 * preprocess folds either into `page` so `page.nextCursor` / `page.total` stay the single source.
 */
export function drataPage<T extends z.ZodType>(item: T) {
  return z.preprocess(
    (v) => {
      if (!isRawObject(v)) return v;
      const pagination = isRawObject(v.pagination) ? v.pagination : null;
      const legacy = isRawObject(v.page) ? v.page : null;
      if (!pagination && legacy) return v;
      const next = pickFirst(pagination?.cursor, legacy?.nextCursor);
      return {
        ...v,
        page: {
          ...(legacy ?? {}),
          nextCursor: typeof next === 'string' && next !== '' ? next : null,
          total: pickFirst(pagination?.totalCount, legacy?.total) ?? null,
        },
      };
    },
    z.object({ data: z.array(item), page: DrataPageMetaSchema, lastCheck: isoDateTime }).loose(),
  );
}
export type DrataPage<T> = { data: T[]; page: DrataPageMeta; lastCheck?: string | null };

/** Legacy (V1-shaped sim) query: `cursor`, `limit` <= 200, `workspaceId`. */
export const DrataPageQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  workspaceId: z.string().optional(),
});
export type DrataPageQuery = z.infer<typeof DrataPageQuerySchema>;

/** real-providers change: verified V2 query (`cursor`, `size` 1..500 default 50, `includeTotalCount`, `expand[]`). */
export const DrataV2PageQuerySchema = z.object({
  cursor: z.string().optional(),
  size: z.coerce.number().int().min(1).max(500).default(50),
  includeTotalCount: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => v === true || v === 'true' || v === '1'),
});
export type DrataV2PageQuery = z.infer<typeof DrataV2PageQuerySchema>;

/** `ExceptionResponsePublicV2Dto`: `{name, statusCode, message, code, debugInfo?}`; `code` may be numeric. */
export const DrataErrorResponseSchema = z
  .object({
    statusCode: z.number().int().optional(),
    message: z.union([z.string(), z.array(z.string())]).optional(),
    error: z.string().optional(),
    name: z.string().optional(),
    code: z.union([z.string(), z.number()]).optional(),
  })
  .loose();
export type DrataErrorResponse = z.infer<typeof DrataErrorResponseSchema>;

function drataWorkspaceAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, rawIdString(raw.id)),
    isDefault: pickFirst(raw.isDefault, raw.primary),
    frameworkIds: Array.isArray(raw.frameworkIds) ? idList(raw.frameworkIds) : Array.isArray(raw.frameworks) ? idsOfObjects(raw.frameworks) : undefined,
  };
}

export const DrataWorkspaceSchema = z.preprocess(
  drataWorkspaceAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      slug: z.string().optional(),
      isDefault: z.boolean().optional(),
      createdAt: isoDateTime,
      frameworkIds: z.array(z.string()).optional(),
    })
    .loose(),
);
export type DrataWorkspace = z.infer<typeof DrataWorkspaceSchema>;

export const DrataFrameworkRefSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    slug: z.string().optional(),
    tag: z.string().optional(),
    requirementIds: z.array(z.string()).optional(),
  })
  .loose();

function drataControlAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const flags = isRawObject(raw.flags) ? raw.flags : null;
  const testIds = isRawObject(raw.testIds) ? raw.testIds : null;
  const evidenceIds = isRawObject(raw.evidenceIds) ? raw.evidenceIds : null;
  const tri = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
  const monitorIds = Array.isArray(raw.monitorIds) ? idList(raw.monitorIds) : testIds ? [...new Set([...idList(testIds.monitored), ...idList(testIds.passing), ...idList(testIds.failing)])] : [];
  const frameworks = Array.isArray(raw.frameworks)
    ? raw.frameworks.map((f) => (typeof f === 'string' ? { id: f } : isRawObject(f) ? { ...f, id: rawIdString(f.id) ?? rawIdString(f.tag) ?? '' } : f))
    : Array.isArray(raw.frameworkTags)
      ? raw.frameworkTags.filter((t): t is string => typeof t === 'string').map((tag) => ({ id: tag, tag }))
      : [];
  return {
    ...raw,
    id: rawIdString(raw.id),
    code: pickFirst(raw.code, rawIdString(raw.id)) ?? '',
    name: pickFirst(raw.name, raw.code, rawIdString(raw.id)),
    isReady: tri(pickFirst(raw.isReady, flags?.isReady)),
    isMonitored: tri(pickFirst(raw.isMonitored, flags?.isMonitored)),
    hasEvidence: tri(pickFirst(raw.hasEvidence, flags?.hasEvidence)),
    inScope: typeof raw.inScope === 'boolean' ? raw.inScope : true,
    frameworks,
    monitorIds,
    evidenceIds: Array.isArray(raw.evidenceIds) ? idList(raw.evidenceIds) : evidenceIds ? [...new Set([...idList(evidenceIds.validExternalEvidenceIds), ...idList(evidenceIds.invalidExternalEvidenceIds)])] : [],
    ownerIds: Array.isArray(raw.ownerIds) ? idList(raw.ownerIds) : isRawObject(raw.owners) ? idsOfObjects(raw.owners) : undefined,
  };
}

export const DrataControlSchema = z.preprocess(
  drataControlAliases,
  z
    .object({
      id: z.string().min(1),
      code: z.string(),
      name: z.string(),
      description: z.string().nullable().optional(),
      isReady: z.boolean().nullable(),
      isMonitored: z.boolean().nullable(),
      hasEvidence: z.boolean().nullable(),
      frameworks: z.array(DrataFrameworkRefSchema).default([]),
      frameworkTags: z.array(z.string()).optional(),
      inScope: z.boolean().nullable().default(true),
      archivedAt: isoDateTime,
      monitorIds: z.array(z.string()).default([]),
      evidenceIds: z.array(z.string()).default([]),
      ownerIds: z.array(z.string()).optional(),
      updatedAt: isoDateTime,
      controlUrl: looseUrl,
    })
    .loose(),
);
export type DrataControl = z.infer<typeof DrataControlSchema>;

/** Legacy values plus the verified V2 `checkResultStatus` values (`READY`, `PREAUDIT`). */
export const DRATA_CHECK_RESULT_STATUSES = ['PASSED', 'FAILED', 'ERROR', 'PENDING', 'UNUSED', 'READY', 'PREAUDIT'] as const;
export const DrataCheckResultStatusSchema = z.enum(DRATA_CHECK_RESULT_STATUSES);
export type DrataCheckResultStatus = z.infer<typeof DrataCheckResultStatusSchema>;

/** Verified V2 `checkStatus` values; DISABLED and UNUSED mean the monitor is not running. */
export const DRATA_CHECK_STATUSES_V2 = ['UNUSED', 'NEW', 'ENABLED', 'DISABLED', 'TESTING'] as const;

function drataMonitorAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const instances = Array.isArray(raw.monitorInstances) ? raw.monitorInstances.filter(isRawObject) : [];
  const firstCheckType = instances.map((i) => (Array.isArray(i.checkTypes) ? i.checkTypes.find((t) => typeof t === 'string') : undefined)).find((t) => typeof t === 'string');
  const checkStatus = typeof raw.checkStatus === 'string' ? raw.checkStatus : null;
  return {
    ...raw,
    id: rawIdString(raw.id),
    testId: rawIdString(raw.testId),
    name: pickFirst(raw.name, rawIdString(raw.id)),
    checkResultStatus: pickFirst(raw.checkResultStatus) ?? null,
    isEnabled: pickFirst(raw.isEnabled, checkStatus ? !(checkStatus === 'DISABLED' || checkStatus === 'UNUSED') : undefined) ?? null,
    controlIds: Array.isArray(raw.controlIds) ? idList(raw.controlIds) : idsOfObjects(raw.controls),
    lastCheck: pickFirst(raw.lastCheck, raw.lastPassedAt, raw.updatedAt) ?? null,
    checkType: pickFirst(raw.checkType, firstCheckType, raw.testSource) ?? null,
  };
}

export const DrataMonitorSchema = z.preprocess(
  drataMonitorAliases,
  z
    .object({
      id: z.string().min(1),
      /** V2: the workspace-scoped id used by the `/monitoring-tests/{testId}/failures` path. */
      testId: z.string().optional(),
      name: z.string(),
      description: z.string().nullable().optional(),
      checkResultStatus: rawStatus,
      checkStatus: z.string().nullable().optional(),
      testSource: z.string().nullable().optional(),
      lastCheck: isoDateTime,
      lastPassedAt: isoDateTime,
      failedSince: isoDateTime,
      controlIds: z.array(z.string()).default([]),
      isEnabled: z.boolean().nullable().optional(),
      checkType: z.string().nullable().optional(),
      failureCount: z.number().int().nonnegative().nullable().optional(),
      monitorUrl: looseUrl,
    })
    .loose(),
);
export type DrataMonitor = z.infer<typeof DrataMonitorSchema>;

function causeText(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (!Array.isArray(v)) return undefined;
  const parts = v.map((c) => (typeof c === 'string' ? c : isRawObject(c) ? (typeof c.message === 'string' ? c.message : typeof c.description === 'string' ? c.description : typeof c.name === 'string' ? c.name : JSON.stringify(c)) : String(c))).filter((s) => s !== '');
  return parts.length > 0 ? parts.join('; ') : undefined;
}

function drataFailureAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const resourceId = pickFirst(raw.resourceId, raw.id, raw.resourceArn);
  return {
    ...raw,
    id: rawIdString(raw.id),
    monitorId: rawIdString(raw.monitorId),
    resourceId: rawIdString(resourceId) ?? '',
    resourceName: pickFirst(raw.resourceName, raw.resourceArn, resourceId) ?? 'unknown resource',
    resourceType: pickFirst(raw.resourceType, raw.providerName) ?? null,
    reason: pickFirst(raw.reason, causeText(raw.cause)) ?? 'Failing in Drata',
    isFixable: typeof raw.isFixable === 'boolean' ? raw.isFixable : null,
    personnelId: rawIdString(raw.personnelId) ?? null,
    severity: typeof raw.severity === 'string' ? raw.severity.toUpperCase() : null,
    status: typeof raw.status === 'string' ? raw.status : null,
  };
}

export const DrataMonitorFailureSchema = z.preprocess(
  drataFailureAliases,
  z
    .object({
      id: z.string().optional(),
      monitorId: z.string().optional(),
      resourceId: z.string(),
      resourceName: z.string(),
      resourceType: z.string().nullable().optional(),
      reason: z.string(),
      /** Drata exposes no fixability: null (UNKNOWN) for real failures. */
      isFixable: z.boolean().nullable(),
      /** V2: `OPEN` | `EXCLUDED` (excluded findings are only returned with `includeExclusions=true`). */
      status: z.string().nullable().optional(),
      providerName: z.string().nullable().optional(),
      accountName: z.string().nullable().optional(),
      region: z.string().nullable().optional(),
      personnelId: z.string().nullable().optional(),
      firstFailedAt: isoDateTime,
      severity: z.string().nullable().optional(),
      dueDate: isoDateTime,
      packageName: z.string().nullable().optional(),
      fixedVersion: z.string().nullable().optional(),
    })
    .loose(),
);
export type DrataMonitorFailure = z.infer<typeof DrataMonitorFailureSchema>;

/** Legacy values plus the verified V2 `/evidence` `status` values. */
export const DRATA_EVIDENCE_STATUSES = ['CURRENT', 'EXPIRED', 'EXPIRING_SOON', 'NEEDS_REVIEW', 'UNKNOWN', 'NEEDS_ARTIFACT', 'ERROR', 'READY', 'NEEDS_SOURCE', 'NEEDS_ATTENTION'] as const;

function drataEvidenceAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const artifacts = isRawObject(raw.artifacts) && Array.isArray(raw.artifacts.data) ? raw.artifacts.data.filter(isRawObject) : [];
  const owners = isRawObject(raw.owners) && Array.isArray(raw.owners.data) ? raw.owners.data.filter(isRawObject) : [];
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, rawIdString(raw.id)),
    type: pickFirst(raw.type, artifacts[0]?.type, artifacts[0]?.artifactType) ?? 'UNKNOWN',
    status: pickFirst(raw.status) ?? null,
    controlIds: Array.isArray(raw.controlIds) ? idList(raw.controlIds) : idsOfObjects(raw.controls),
    ownerId: pickFirst(raw.ownerId, owners[0] ? rawIdString(owners[0].id) : undefined) ?? null,
    evidenceUrl: pickFirst(raw.evidenceUrl, raw.url) ?? null,
  };
}

export const DrataEvidenceSchema = z.preprocess(
  drataEvidenceAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      description: z.string().nullable().optional(),
      type: z.string(),
      status: rawStatus,
      controlIds: z.array(z.string()).default([]),
      renewalDate: isoDateTime,
      renewalScheduleType: z.string().nullable().optional(),
      updatedAt: isoDateTime,
      ownerId: z.string().nullable().optional(),
      evidenceUrl: looseUrl,
    })
    .loose(),
);
export type DrataEvidence = z.infer<typeof DrataEvidenceSchema>;

function drataFrameworkAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  return {
    ...raw,
    id: rawIdString(raw.id),
    name: pickFirst(raw.name, raw.slug, raw.tag, rawIdString(raw.id)),
    controlsReady: pickFirst(raw.controlsReady, raw.numReadyInScopeRequirements) ?? null,
    controlsTotal: pickFirst(raw.controlsTotal, raw.numInScopeRequirements) ?? null,
  };
}

export const DrataFrameworkSchema = z.preprocess(
  drataFrameworkAliases,
  z
    .object({
      id: z.string().min(1),
      name: z.string(),
      slug: z.string().optional(),
      /** V2 framework tag (`SOC_2`, `ISO27001`, ...); controls reference frameworks by this tag. */
      tag: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      controlsReady: z.number().int().nonnegative().nullable().optional(),
      controlsTotal: z.number().int().nonnegative().nullable().optional(),
      numInScopeControls: z.number().int().nonnegative().nullable().optional(),
      monitorsPassing: z.number().int().nonnegative().nullable().optional(),
      monitorsTotal: z.number().int().nonnegative().nullable().optional(),
      isEnabled: z.boolean().optional(),
      isReady: z.boolean().nullable().optional(),
    })
    .loose(),
);
export type DrataFramework = z.infer<typeof DrataFrameworkSchema>;

export const DRATA_EMPLOYMENT_STATUSES = [
  'CURRENT_EMPLOYEE',
  'CURRENT_CONTRACTOR',
  'FUTURE_HIRE',
  'FORMER_EMPLOYEE',
  'FORMER_CONTRACTOR',
  'OUT_OF_SCOPE',
  'SERVICE_ACCOUNT',
] as const;
export const DrataEmploymentStatusSchema = z.enum(DRATA_EMPLOYMENT_STATUSES);
export type DrataEmploymentStatus = z.infer<typeof DrataEmploymentStatusSchema>;

/** real-providers change: the verified V2 `employmentStatus` values beyond the legacy list (the fixtures keep the list above). */
export const DRATA_REAL_EMPLOYMENT_STATUSES = ['UNKNOWN', 'SPECIAL_FORMER_EMPLOYEE', 'SPECIAL_FORMER_CONTRACTOR'] as const;

export const DRATA_CHECK_STATUSES = ['PASS', 'FAIL', 'UNKNOWN'] as const;
export const DrataCheckStatusSchema = z.enum(DRATA_CHECK_STATUSES);
export type DrataCheckStatus = z.infer<typeof DrataCheckStatusSchema>;

/** Verified V2 `complianceChecks[].type` → legacy check key. */
export const DRATA_COMPLIANCE_CHECK_TYPES: Readonly<Record<string, string>> = {
  ACCEPTED_POLICIES: 'policiesAccepted',
  SECURITY_TRAINING: 'securityTraining',
  HIPAA_TRAINING: 'hipaaTraining',
  BG_CHECK: 'backgroundCheck',
  IDENTITY_MFA: 'mfa',
  AGENT_INSTALLED: 'agentInstalled',
  PASSWORD_MANAGER: 'passwordManager',
  HDD_ENCRYPTION: 'diskEncryption',
  ANTIVIRUS: 'antivirus',
  AUTO_UPDATES: 'autoUpdates',
  LOCK_SCREEN: 'screenLock',
};

/** Each check is PASS | FAIL | UNKNOWN (or NOT_APPLICABLE for excluded checks) or absent (= not reported). */
const checkValue = z.string().optional();
export const DrataComplianceChecksSchema = z
  .object({
    policiesAccepted: checkValue,
    securityTraining: checkValue,
    hipaaTraining: checkValue,
    backgroundCheck: checkValue,
    mfa: checkValue,
    agentInstalled: checkValue,
    passwordManager: checkValue,
    diskEncryption: checkValue,
    antivirus: checkValue,
    autoUpdates: checkValue,
    screenLock: checkValue,
  })
  .loose();
export type DrataComplianceChecks = z.infer<typeof DrataComplianceChecksSchema>;

/** V2 `complianceChecks[{type,status}]` → legacy check map; PASS/FAIL kept, EXCLUDED → NOT_APPLICABLE, else UNKNOWN. */
function complianceChecksFromArray(list: unknown[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const item of list) {
    if (!isRawObject(item) || typeof item.type !== 'string') continue;
    const key = DRATA_COMPLIANCE_CHECK_TYPES[item.type];
    if (!key) continue;
    const status = item.status;
    out[key] = status === 'PASS' || status === 'FAIL' ? status : status === 'EXCLUDED' ? 'NOT_APPLICABLE' : 'UNKNOWN';
  }
  return out;
}

function drataPersonnelAliases(raw: unknown): unknown {
  if (!isRawObject(raw)) return raw;
  const user = isRawObject(raw.user) ? raw.user : null;
  const checks = Array.isArray(raw.complianceChecks) ? complianceChecksFromArray(raw.complianceChecks) : raw.complianceChecks;
  return {
    ...raw,
    id: rawIdString(raw.id),
    email: pickFirst(raw.email, user?.email) ?? '',
    firstName: pickFirst(raw.firstName, user?.firstName) ?? null,
    lastName: pickFirst(raw.lastName, user?.lastName) ?? null,
    employmentStatus: pickFirst(raw.employmentStatus) ?? null,
    startDate: pickFirst(raw.startDate, raw.startedAt) ?? null,
    separationDate: pickFirst(raw.separationDate, raw.separatedAt) ?? null,
    complianceChecks: checks ?? null,
    complianceCheckDetails: Array.isArray(raw.complianceChecks) ? raw.complianceChecks : raw.complianceCheckDetails,
    deviceIds: Array.isArray(raw.deviceIds) ? idList(raw.deviceIds) : undefined,
    userId: rawIdString(pickFirst(raw.userId, user?.id)) ?? null,
  };
}

export const DrataPersonnelSchema = z.preprocess(
  drataPersonnelAliases,
  z
    .object({
      id: z.string().min(1),
      email: z.string(),
      firstName: z.string().nullable().optional(),
      lastName: z.string().nullable().optional(),
      employmentStatus: rawStatus,
      startDate: isoDateTime,
      separationDate: isoDateTime,
      jobTitle: z.string().nullable().optional(),
      complianceChecks: DrataComplianceChecksSchema.nullable().optional(),
      deviceIds: z.array(z.string()).optional(),
      userId: z.string().nullable().optional(),
    })
    .loose(),
);
export type DrataPersonnel = z.infer<typeof DrataPersonnelSchema>;

/** real-providers change: `GET /company` (permission `company-settings-get-info`) for the tenant identity. */
export const DrataCompanySchema = z.preprocess(
  (v) => (isRawObject(v) ? { ...v, accountId: rawIdString(v.accountId) } : v),
  z
    .object({
      accountId: z.string().min(1).optional(),
      name: z.string().nullable().optional(),
      legalName: z.string().nullable().optional(),
      domain: z.string().nullable().optional(),
    })
    .loose(),
);
export type DrataCompany = z.infer<typeof DrataCompanySchema>;

export const DrataWorkspacesPageSchema = drataPage(DrataWorkspaceSchema);
export const DrataControlsPageSchema = drataPage(DrataControlSchema);
export const DrataMonitorsPageSchema = drataPage(DrataMonitorSchema);
export const DrataMonitorFailuresPageSchema = drataPage(DrataMonitorFailureSchema);
export const DrataEvidencePageSchema = drataPage(DrataEvidenceSchema);
export const DrataFrameworksPageSchema = drataPage(DrataFrameworkSchema);
export const DrataPersonnelPageSchema = drataPage(DrataPersonnelSchema);

/** Legacy V1-shaped templates (the sim's `/public/*` routes and the renderer mock keep them). */
export const DRATA_ENDPOINTS = {
  workspaces: '/public/workspaces',
  controls: '/public/controls',
  control: '/public/controls/{controlId}',
  controlTests: '/public/controls/{controlId}/tests',
  monitors: '/public/monitors',
  monitorFailures: '/public/monitors/{monitorId}/failures',
  frameworks: '/public/frameworks',
  personnel: '/public/personnel',
  evidenceLibrary: '/public/evidence-library',
} as const;
export type DrataEndpoint = (typeof DRATA_ENDPOINTS)[keyof typeof DRATA_ENDPOINTS];

/**
 * real-providers change: verified V2 templates (PROVIDER_API_NOTES section 2.5), relative to the
 * regional host. The workspace is a path segment; personnel and company are account-level.
 */
export const DRATA_V2_ENDPOINTS = {
  workspaces: '/public/v2/workspaces',
  controls: '/public/v2/workspaces/{workspaceId}/controls',
  frameworks: '/public/v2/workspaces/{workspaceId}/frameworks',
  monitors: '/public/v2/workspaces/{workspaceId}/monitoring-tests',
  monitorFailures: '/public/v2/workspaces/{workspaceId}/monitoring-tests/{testId}/failures',
  evidence: '/public/v2/workspaces/{workspaceId}/evidence',
  personnel: '/public/v2/personnel',
  company: '/public/v2/company',
} as const;
export type DrataV2Endpoint = (typeof DRATA_V2_ENDPOINTS)[keyof typeof DRATA_V2_ENDPOINTS];

/** `expand[]` values the adapter sends so the V2 payloads carry the fields the normalizers need. */
export const DRATA_V2_EXPANDS: Readonly<Record<'controls' | 'monitors' | 'evidence' | 'personnel' | 'workspaces', readonly string[]>> = {
  workspaces: ['frameworks'],
  controls: ['flags', 'testIds', 'evidenceIds', 'frameworkTags', 'requirements', 'owners'],
  monitors: ['controls', 'monitorInstances'],
  evidence: ['controls', 'owners', 'artifacts'],
  personnel: ['user', 'complianceChecks'],
};

/** Documented limits (spec section 6): 500 requests/min per source IP; V2 page size up to 500. */
export const DRATA_LIMITS = {
  documentedRequestsPerMinutePerIp: 500,
  defaultCeilingPerMinute: 120,
  maxPageSize: 500,
} as const;

// ---------------------------------------------------------------------------------------------
// real-providers change: verified regional hosts (PROVIDER_API_NOTES section 2.1)
// ---------------------------------------------------------------------------------------------

export const DRATA_REGIONS = ['us', 'eu', 'apac'] as const;
export type DrataRegion = (typeof DRATA_REGIONS)[number];
export const DrataRegionSchema = z.enum(DRATA_REGIONS);

export const DRATA_REGION_BASE_URLS: Readonly<Record<DrataRegion, string>> = {
  us: 'https://public-api.drata.com',
  eu: 'https://public-api.eu.drata.com',
  apac: 'https://public-api.apac.drata.com',
};

export const DRATA_REGION_LABELS: Readonly<Record<DrataRegion, string>> = {
  us: 'US · public-api.drata.com',
  eu: 'EU · public-api.eu.drata.com',
  apac: 'APAC · public-api.apac.drata.com',
};

export const DRATA_API_HOSTS: readonly string[] = ['public-api.drata.com', 'public-api.eu.drata.com', 'public-api.apac.drata.com'];
